// @ts-nocheck
/**
 * Destination-side category gradients. The mapping agent emits these
 * category names on each classified account, and both the inline
 * classify_section result widget and the final mapping card render them
 * as badges. Keeping the palette in one place means a tweak propagates
 * to every widget that displays a category.
 *
 * Mirrors the gradients used by ResultsDisplayMulti's .category-combo
 * CSS so the two surfaces share a visual language. Sister file to
 * sectionPalette.ts, which colors the *source* sections (Revenue, COGS,
 * OpEx, …) rather than the destination categories.
 */

const CATEGORY_GRADIENT: Record<string, [string, string]> = {
  'Revenue':                           ['#10b981', '#059669'],
  'Cost of Sales':                     ['#f97316', '#ea580c'],
  'Operating Expenses':                ['#f59e0b', '#d97706'],
  'Payroll Expenses':                  ['#f59e0b', '#d97706'],
  'Advertising':                       ['#f97316', '#ea580c'],
  'General & Administrative Expenses': ['#fbbf24', '#f59e0b'],
  'Insurance':                         ['#06b6d4', '#0891b2'],
  'Legal & Professional Services':     ['#8b5cf6', '#7c3aed'],
  'Rent & Lease':                      ['#6366f1', '#4f46e5'],
  'Taxes & Licenses':                  ['#ec4899', '#db2777'],
  'Travel & Entertainment':            ['#10b981', '#059669'],
  'Other Business Expenses':           ['#64748b', '#475569'],
  'Non-Recurring Expense':             ['#fb7185', '#f43f5e'],
  'Other Income':                      ['#14b8a6', '#0d9488'],
  'Other Expenses':                    ['#a855f7', '#9333ea'],
  'Amortization':                      ['#a855f7', '#9333ea'],
  'Depreciation Expense':              ['#a855f7', '#9333ea'],
  'Interest Expense':                  ['#0ea5e9', '#0284c7'],
  'Interest Income':                   ['#38bdf8', '#0ea5e9'],
  'Interest Net':                      ['#0ea5e9', '#0284c7'],
  'State PTE Taxes':                   ['#ef4444', '#dc2626'],
  'Uncategorized':                     ['#6b7280', '#4b5563'],
};

const DEFAULT_GRADIENT: [string, string] = ['#6b7280', '#4b5563'];

export const CATEGORY_ORDER = [
  'Revenue',
  'Cost of Sales',
  'Operating Expenses',
  'Payroll Expenses',
  'Advertising',
  'General & Administrative Expenses',
  'Insurance',
  'Legal & Professional Services',
  'Rent & Lease',
  'Taxes & Licenses',
  'Travel & Entertainment',
  'Other Business Expenses',
  'Non-Recurring Expense',
  'Other Income',
  'Other Expenses',
  'Depreciation Expense',
  'Amortization',
  'Interest Expense',
  'Interest Income',
  // Legacy pre-split leaf: kept so old sessions' rows still sort and
  // group here, but new classifications always use the two leaves above.
  'Interest Net',
  'State PTE Taxes',
];

// Categories a user may PICK in the mapping card's category dropdown.
// Legacy pre-split names stay in CATEGORY_ORDER so existing rows keep their
// sort position, grouping, and gradient, but they are no longer offered as
// choices: a legacy row shows its current value pinned at the top of its own
// dropdown (the unknown-value fallback) until the user moves it to a live
// leaf. 'Interest Net' was superseded by Interest Expense / Interest Income.
const LEGACY_ONLY_CATEGORIES = new Set(['Interest Net']);
export const SELECTABLE_CATEGORY_ORDER = CATEGORY_ORDER.filter(
  (c) => !LEGACY_ONLY_CATEGORIES.has(c),
);

export function categoryStyle(category: string | undefined | null) {
  const [from, to] = CATEGORY_GRADIENT[category ?? ''] || DEFAULT_GRADIENT;
  return {
    background: `linear-gradient(135deg, ${from}, ${to})`,
    borderColor: to,
  };
}

// P&L rollup. The agent emits leaf categories (Payroll Expenses,
// Advertising, Insurance, …) which are all subcategories of
// Operating Expenses in standard P&L presentation. Group headers in the
// FinalMappingCard collapse those leaves under their parent so the
// table reads like a real income statement; per-row badges still show
// the leaf category for granularity.
const PARENT_OF: Record<string, string> = {
  'Revenue': 'Revenue',
  'Cost of Sales': 'Cost of Sales',
  'Operating Expenses': 'Operating Expenses',
  'Payroll Expenses': 'Operating Expenses',
  'Advertising': 'Operating Expenses',
  'General & Administrative Expenses': 'Operating Expenses',
  'Insurance': 'Operating Expenses',
  'Legal & Professional Services': 'Operating Expenses',
  'Rent & Lease': 'Operating Expenses',
  'Taxes & Licenses': 'Operating Expenses',
  'Travel & Entertainment': 'Operating Expenses',
  'Other Business Expenses': 'Operating Expenses',
  'Non-Recurring Expense': 'Operating Expenses',
  'Other Income': 'Other Income',
  'Other Expenses': 'Other Expenses',
  'Depreciation Expense': 'Depreciation Expense',
  'Amortization': 'Amortization',
  'Interest Expense': 'Interest Net',
  'Interest Income': 'Interest Net',
  'Interest Net': 'Interest Net',
  'State PTE Taxes': 'State PTE Taxes',
  'Uncategorized': 'Uncategorized',
};

export const PARENT_ORDER = [
  'Revenue',
  'Cost of Sales',
  'Operating Expenses',
  'Other Income',
  'Other Expenses',
  'Depreciation Expense',
  'Amortization',
  'Interest Net',
  'State PTE Taxes',
];

export function parentCategory(category: string | undefined | null): string {
  if (!category) return 'Uncategorized';
  return PARENT_OF[category] || category;
}

const LEAF_ORDER_INDEX = new Map(CATEGORY_ORDER.map((c, i) => [c, i]));
export function leafOrderIndex(category: string | undefined | null): number {
  return LEAF_ORDER_INDEX.get(category ?? '') ?? Number.MAX_SAFE_INTEGER;
}

// ---------------------------------------------------------------------------
// Balance-sheet destination buckets (mirrors the backend's
// BS_DEFAULT_STRUCTURE taxonomy — 12 buckets, assets → liabilities → equity).
// Hue families encode the section so a badge's color tells you which side of
// A = L + E an account sits on: assets in greens/teals, liabilities in warm
// oranges/reds, equity in violets. Each bucket keeps a distinct gradient
// within its family, all drawn from the same stops the P&L palette uses.
// ---------------------------------------------------------------------------

const BS_CATEGORY_GRADIENT: Record<string, [string, string]> = {
  // Assets — greens / teals
  'Cash Equivalent':                          ['#10b981', '#059669'],
  'Accounts Receivable':                      ['#14b8a6', '#0d9488'],
  'Inventory':                                ['#06b6d4', '#0891b2'],
  'Other Current Assets':                     ['#38bdf8', '#0ea5e9'],
  'Fixed Asset':                              ['#16a34a', '#15803d'],
  'Other Asset':                              ['#0f766e', '#115e59'],
  // Liabilities — warm oranges / reds
  'Accounts Payable':                         ['#f97316', '#ea580c'],
  'Credit Card':                              ['#fb7185', '#f43f5e'],
  'Other Current Liability':                  ['#f59e0b', '#d97706'],
  'Long Term Liability':                      ['#ef4444', '#dc2626'],
  // Equity — violets
  "Member's Contributions/(Distributions)":   ['#8b5cf6', '#7c3aed'],
  "Member's Equity":                          ['#6366f1', '#4f46e5'],
};

export const BS_CATEGORY_ORDER = [
  'Cash Equivalent',
  'Accounts Receivable',
  'Inventory',
  'Other Current Assets',
  'Fixed Asset',
  'Other Asset',
  'Accounts Payable',
  'Credit Card',
  'Other Current Liability',
  'Long Term Liability',
  "Member's Contributions/(Distributions)",
  "Member's Equity",
];

// All 12 buckets are live choices (no legacy-only names on the BS side yet).
// A row whose category falls outside this list — e.g. Uncategorized — shows
// its current value pinned at the top of its own dropdown, same as the P&L.
export const SELECTABLE_BS_CATEGORY_ORDER = BS_CATEGORY_ORDER;

export function bsCategoryStyle(category: string | undefined | null) {
  const [from, to] = BS_CATEGORY_GRADIENT[category ?? ''] || DEFAULT_GRADIENT;
  return {
    background: `linear-gradient(135deg, ${from}, ${to})`,
    borderColor: to,
  };
}
